import { useEffect, useState } from "react";
import DBFieldBlock from "../../../../component/global/DBFieldBlock";
import DBInput from "../../../../component/global/DBInput";
import DBSelect from "../../../../component/global/DBSelect";
import DBTagInput from "../../../../component/global/DBTagInput";
import DBUpload from "../../../../component/global/DBUpload";
import {
    parseFeatureOptions,
    parseFeatureValueList,
    toBase64,
} from "../../../../feedback/helperFunctions";
import { t } from "i18next";

const LinkInput = ({ value, onChange }) => {
    const [caption, setCaption] = useState("");
    const [url, setUrl] = useState("");

    useEffect(() => {
        if (value) {
            const div = document.createElement("DIV");
            div.innerHTML = value;

            const link = div.querySelector("a");
            if (link) {
                setCaption(link.innerHTML);
                setUrl(link.getAttribute("href"));
            }
        }
    }, [value]);

    const linkBuilder = () => {
        if (url.trim() || caption.trim())
            onChange(
                `<a href="${url.trim()}" target="_blank">${
                    caption.trim() || t("feature.link.default")
                }</a>`
            );
    };

    useEffect(() => {
        linkBuilder();
    }, [url, caption]);

    return (
        <div className="link-feature-container">
            <DBInput
                placeholder="عنوان لینک"
                type="text"
                size="large"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
            />
            <DBInput
                placeholder="آدرس لینک"
                type="text"
                size="large"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
            />
        </div>
    );
};

function featureValue(values, featureId) {
    if (!Array.isArray(values)) return "";
    return values.find((row) => row.feature === featureId)?.value || "";
}

function replaceFeatureValue(values, featureId, nextValue) {
    const rest = (Array.isArray(values) ? values : []).filter(
        (row) => row.feature !== featureId
    );
    if (!nextValue) return rest;
    return [...rest, { feature: featureId, value: nextValue }];
}

function SelectFeatureField({ feature, value, onChange }) {
    const raw = featureValue(value, feature.feature);
    const stored = parseFeatureValueList(raw);
    const options = parseFeatureOptions(feature.feature_options);

    if (options.length > 0 && stored.length <= 1) {
        const current = stored[0] || "";
        const matched = options.find(
            (option) => option.value === current || option.label === current
        );
        const choices = options.map((option) => ({
            value: option.value,
            label: option.label,
        }));
        if (current && !matched) {
            choices.unshift({ value: current, label: current });
        }

        return (
            <DBFieldBlock title={feature.name}>
                <DBSelect
                    size="large"
                    allowClear
                    placeholder={t("choose.one")}
                    value={matched ? matched.value : current || undefined}
                    options={choices}
                    onChange={(selected) => {
                        if (selected == null || selected === "") {
                            onChange(replaceFeatureValue(value, feature.feature, ""));
                            return;
                        }
                        const option = choices.find(
                            (item) => item.value === selected
                        );
                        onChange(
                            replaceFeatureValue(
                                value,
                                feature.feature,
                                option?.label || selected
                            )
                        );
                    }}
                />
            </DBFieldBlock>
        );
    }

    return (
        <DBFieldBlock title={feature.name}>
            <DBTagInput
                value={stored.map((item, index) => ({
                    id: index + 1,
                    caption: item,
                }))}
                onChange={(tags) =>
                    onChange(
                        replaceFeatureValue(
                            value,
                            feature.feature,
                            JSON.stringify(tags.map((item) => item.caption))
                        )
                    )
                }
            />
        </DBFieldBlock>
    );
}

export default function FeaturesList({ features, value, onChange }) {
    return (
        <>
            {(Array.isArray(features) ? features : [])
                .filter((feature) => !feature.is_fixed)
                .map((feature, index) => {
                switch (feature.feature_type) {
                    case 1:
                        return (
                            <DBFieldBlock title={feature.name} key={index}>
                                <DBInput
                                    type="text"
                                    size="large"
                                    value={
                                        value.find(
                                            (r) => feature.feature === r.feature
                                        )?.value || ""
                                    }
                                    onChange={(e) =>
                                        onChange([
                                            ...value.filter(
                                                (r) =>
                                                    r.feature !==
                                                    feature.feature
                                            ),
                                            {
                                                feature: feature.feature,
                                                value: e.target.value,
                                            },
                                        ])
                                    }
                                />
                            </DBFieldBlock>
                        );
                    case 2:
                        return (
                            <DBFieldBlock title={feature.name} key={index}>
                                <DBUpload
                                    accept=".jpg,.jpeg,.pdf,.zip"
                                    maxSize={1024 * 1024}
                                    outlined
                                    caption={
                                        value.find(
                                            (r) => r.feature === feature.feature
                                        )?.value
                                            ? t("feature.file.delete")
                                            : t("feature.file.upload")
                                    }
                                    type={
                                        value.find(
                                            (r) => r.feature === feature.feature
                                        )?.value
                                            ? "secondary"
                                            : "primary"
                                    }
                                    onClick={(e) => {
                                        if (
                                            value.find(
                                                (r) =>
                                                    r.feature ===
                                                    feature.feature
                                            )?.value
                                        ) {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            onChange([
                                                ...value.filter(
                                                    (r) =>
                                                        r.feature !==
                                                        feature.feature
                                                ),
                                            ]);
                                        }
                                    }}
                                    onChange={async (e) =>
                                        onChange([
                                            ...value.filter(
                                                (r) =>
                                                    r.feature !==
                                                    feature.feature
                                            ),
                                            {
                                                feature: feature.feature,
                                                value: await toBase64(
                                                    e.target.files[0]
                                                ),
                                            },
                                        ])
                                    }
                                />
                            </DBFieldBlock>
                        );
                    case 4:
                        return (
                            <DBFieldBlock title={feature.name} key={index}>
                                <DBInput
                                    type="number"
                                    size="large"
                                    value={
                                        value.find(
                                            (r) => r.feature === feature.feature
                                        )?.value || ""
                                    }
                                    onChange={(e) =>
                                        onChange([
                                            ...value.filter(
                                                (r) =>
                                                    r.feature !==
                                                    feature.feature
                                            ),
                                            {
                                                feature: feature.feature,
                                                value: e.target.value,
                                            },
                                        ])
                                    }
                                />
                            </DBFieldBlock>
                        );

                    case 5:
                        return (
                            <SelectFeatureField
                                key={index}
                                feature={feature}
                                value={value}
                                onChange={onChange}
                            />
                        );
                    case 6:
                        return (
                            <DBFieldBlock title={feature.name} key={index}>
                                <LinkInput
                                    value={
                                        value.find(
                                            (r) => r.feature === feature.feature
                                        )?.value || ""
                                    }
                                    onChange={(e) =>
                                        onChange([
                                            ...value.filter(
                                                (r) =>
                                                    r.feature !==
                                                    feature.feature
                                            ),
                                            {
                                                feature: feature.feature,
                                                value: e,
                                            },
                                        ])
                                    }
                                />
                            </DBFieldBlock>
                        );
                }
                // return (
                //     <>
                //         <DBTagInput />
                //         <div>{feature.name}</div>
                //     </>
                // );
            })}
        </>
    );
}
